import React, { useEffect, useState } from 'react';
import { ListboxDropdown, SearchBox } from '@bb-ui/react-library';
import { apiUrl } from 'utils/apiUrl';
import useRestApi from 'hooks/useRestApi';
import { ArtifactApprovalType, ArtifactType, IArtifactData } from 'App.types';
import { useAppConfigContext } from 'contexts/AppConfigContext';
import { artifactStageFor } from 'utils/artifactStage';

export interface ApprovedArtifactListsProps {
  defaultVersion: string;
  updateFormData: (version: string) => void;
}

const RESULTS_COUNT = 500;
const APPROVAL_STATUS: ArtifactApprovalType = 'yes';
const ARTIFACT_TYPE: ArtifactType = 'learn-installer';

// get artifact version from artifact data
const getVersion = (artifact: IArtifactData): string =>
  artifact.artifact_basename
    ? artifact.artifact_basename.replace(/\.zip$/, '')
    : artifact.artifact_key.split('/')[2].replace(/\.zip$/, '');

/**
 * Approved Artifact Drop Down to select approved artifacts in release stage
 */
export const ApprovedArtifactDropDown: React.FunctionComponent<ApprovedArtifactListsProps> = ({
  defaultVersion,
  updateFormData,
}) => {
  const appConfig = useAppConfigContext();
  const [artiactVersion, setArtifactVersion] = useState<string>(defaultVersion);
  const [approvedArtifacts, setApprovedArtifacts] = useState<IArtifactData[]>([]);
  const [filteredOptions, setFilteredOptions] = useState<IArtifactData[]>([]);

  // Update artifact data and filter options state
  const setArifactData = (approvedArtifact: IArtifactData[]): void => {
    setApprovedArtifacts(approvedArtifact || []);
    setFilteredOptions(approvedArtifact || []);
  };

  const searchQueryParams = new URLSearchParams({
    artifact_name: `${ARTIFACT_TYPE}/${artifactStageFor(appConfig.stage)}`,
    results_count: RESULTS_COUNT.toString(),
    approval: APPROVAL_STATUS,
  });

  const searchUrl = apiUrl('fcc', `artifacts?${searchQueryParams.toString()}`);

  const { data, loading } = useRestApi(searchUrl);

  useEffect(() => {
    if (data) setArifactData(data);
  }, [data]);

  // filter options based on user input
  const filterOptions = (inputValue: string): void => {
    if (filteredOptions && filteredOptions.length) {
      const filtered = filteredOptions.filter((artifact: IArtifactData) =>
        artifact.artifact_key.includes(inputValue),
      );
      setFilteredOptions(filtered);
    } else {
      setFilteredOptions(approvedArtifacts);
    }
  };

  // handle when selecting option from dropdown
  const handleOptionSelected = (option: IArtifactData): void => {
    const version = getVersion(option);
    setArtifactVersion(version);
    setFilteredOptions(approvedArtifacts);
    updateFormData(version);
  };

  return (
    <ListboxDropdown
      id="approved-artifact-list"
      variant="inline"
      openOnFocus={true}
      keepSelectedOption={false}
      strings={{
        loadingAriaLabel: 'loading',
      }}
      loading={loading}
      onOptionSelected={handleOptionSelected}
      options={filteredOptions}
      renderInput={({ onKeyDown, onFocus, onBlur, inputRef, ariaProps }) => (
        <SearchBox
          hideIconOnFocus={false}
          inputRef={inputRef}
          onChange={(text) => {
            setArtifactVersion(text);
            filterOptions(text);
          }}
          onKeyDown={onKeyDown}
          onFocus={onFocus}
          onBlur={onBlur}
          onEscape={onKeyDown}
          InputProps={{
            inputProps: {
              ...ariaProps,
            },
          }}
          onClear={() => setArtifactVersion('')}
          value={artiactVersion}
          variant="default"
          label="Search"
          inputId="approved-artifact-search"
        />
      )}
      renderOption={(artifact: IArtifactData) => <span>{getVersion(artifact)}</span>}
    />
  );
};
